/**
 * Утилиты для работы с ошибками API
 */

/**
 * Тип ошибки от axios
 */
interface AxiosError {
    response?: {
        data?: {
            message?: string;
        };
    };
    message?: string;
}

/**
 * Извлекает сообщение об ошибке из объекта ошибки API (axios)
 * @param error - Объект ошибки (может быть axios error или обычная ошибка)
 * @param defaultMessage - Сообщение по умолчанию, если не удалось извлечь сообщение
 * @returns Сообщение об ошибке
 */
export const getApiErrorMessage = (error: unknown, defaultMessage: string = 'Произошла ошибка'): string => {
    const err = error as AxiosError;
    return err.response?.data?.message ?? err.message ?? defaultMessage;
};

/** Общий текст бэкенда для 500 — медику он ничего не говорит. */
const SERVER_ERROR_MESSAGE = 'Произошла внутренняя ошибка сервера.';
const CYRILLIC_PATTERN = /[а-яё]/i;
const TECHNICAL_PATTERNS = [/localhost/i, /:\/\//, /exception/i, /\bat\s+\S+\.\S+\(/];
const MAX_READABLE_LENGTH = 300;

/**
 * Можно ли показать сообщение об ошибке пользователю как есть.
 * Технические тексты (английские сообщения исключений, адреса сервисов, стек) — нельзя.
 */
export const isReadableErrorMessage = (message?: string | null): message is string => {
    const text = message?.trim();
    if (!text || text.length > MAX_READABLE_LENGTH || text === SERVER_ERROR_MESSAGE) {
        return false;
    }
    if (!CYRILLIC_PATTERN.test(text)) {
        return false;
    }
    return !TECHNICAL_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * Сводит ошибки в короткий текст: технические заменяются на fallback, одинаковые не повторяются.
 * @param messages - Сообщения об ошибках
 * @param fallback - Текст вместо технического сообщения
 */
export const summarizeErrorMessages = (messages: (string | null | undefined)[], fallback: string): string => {
    const readable = messages.map(message => isReadableErrorMessage(message) ? message.trim() : fallback);
    return Array.from(new Set(readable.length > 0 ? readable : [fallback])).join('\n');
};
